import { FunctionComponent, useEffect, useState } from 'react';
import i18n from 'i18n';
import { useDispatch } from 'react-redux';
import NewswireServiceTable from 'pages/newswires/components/NewswireServices';
import Form from 'pages/administration/newswires/components/Form';
import ItemDetail from 'pages/administration/newswires/components/ItemDetail';
import {
  fetchNewswireServices,
  fetchNewswireFormats,
  createNewswireService,
  updateNewswireService,
  deleteNewswireService
} from 'pages/newswires/actions';
import toggleNotification from 'actions/notifications';
import { Dialog, Button } from '@material-ui/core';
import TitleWrapper from 'components/TitleWrapper';
import ContentBox from 'components/ContentBox';
import ContentBoxItem from 'components/ContentBoxItem';
import Search from 'components/Search';

const Newswires: FunctionComponent = () => {
  const dispatch = useDispatch();

  const handleApiError = () => {
    dispatch(toggleNotification(i18n.t('notifications.apiError'), 'error'));
  };

  // LIST OF NEWSWIRE SERVICES, and the FX to GET THEM
  const [newswireServices, setNewswireServices] = useState<
    $Lns.NewswireService[]
  >([]);

  const [newswiresOrig, setNewswiresOrig] = useState<$Lns.NewswireService[]>(
    []
  );

  const fetchSetNewswireServices = () => {
    fetchNewswireServices().then(({ data }) => {
      setNewswireServices(data);
      setNewswiresOrig(data);
    });
  };

  // LIST of NEWSWIRE FORMATS, needed to match format GUID vs Names
  const [formatList, setFormatList] = useState<$Lns.NewswireFormat[]>([]);

  // STATE to SHOW NEWSWIRE SERVICES, and the CB to change STATES
  const [showNewswireService, setShowNewswireService] =
    useState<$Lns.NewswireService>();

  const findFormatName = (formatSearchGuid: string): string => {
    const formatObj = formatList.find(o => o.guid === formatSearchGuid);
    return formatObj?.name || 'Format Not Found';
  };

  const onShow = (item: $Lns.NewswireService) => {
    setShowNewswireService(item);
  };

  const onClose = () => {
    setShowNewswireService(undefined);
  };

  // State to ADD/EDIT NEWSWIRE SERVICES, and the CB to manage the STATES
  const [editNewswireService, setEditNewswireService] =
    useState<$Lns.NewswireService>();

  const onAdd = () => {
    const newNewswire = {} as $Lns.NewswireService;
    setEditNewswireService(newNewswire);
  };

  const onEdit = (item: $Lns.NewswireService) => {
    const temp = item;
    if (temp.username === '') delete temp.username;
    if (temp.password === '') delete temp.password;
    setEditNewswireService(temp);
  };

  const onEditCancel = () => {
    setEditNewswireService(undefined);
  };

  const onSave = (formData: $Lns.NewswireService) => {
    if (formData.guid === undefined) {
      createNewswireService(formData)
        .then(() => {
          dispatch(
            toggleNotification(
              i18n.t('notifications.newswires.addSuccess'),
              'success'
            )
          );
          fetchSetNewswireServices();
        })
        .catch(handleApiError);
    } else {
      updateNewswireService(formData)
        .then(() => {
          dispatch(
            toggleNotification(
              i18n.t('notifications.newswires.editSuccess'),
              'success'
            )
          );
          fetchSetNewswireServices();
        })
        .catch(handleApiError);
    }
    setEditNewswireService(undefined);
  };

  // CB to DELETE a NEWSWIRE SERVICE
  const onDelete = (newswireServiceGuid: string) => {
    deleteNewswireService(newswireServiceGuid).then(res => {
      if (res && res.code === 500) {
        dispatch(
          toggleNotification(
            res.message || i18n.t('pages.administration.newswires.error'),
            'error'
          )
        );
      } else {
        dispatch(
          toggleNotification(
            i18n.t('notifications.newswires.deleteSuccess'),
            'success'
          )
        );
      }
      fetchSetNewswireServices();
    });
  };

  useEffect(() => {
    fetchSetNewswireServices();
    fetchNewswireFormats().then(({ data }) => {
      setFormatList(data);
    });
  }, []);

  return (
    <ContentBox>
      <ContentBoxItem title>
        <TitleWrapper
          title={i18n.t('links.sidebarNestedItems.administration.newswires')}
        />
        <Button color="primary" variant="contained" onClick={onAdd}>
          {i18n.t('pages.administration.newswires.button.add')}
        </Button>
      </ContentBoxItem>
      <ContentBoxItem>
        <div />
        <Search
          fields={newswiresOrig}
          searchKeys={['guid', 'name']}
          filterFields={setNewswireServices}
        />
      </ContentBoxItem>

      <NewswireServiceTable
        onShow={onShow}
        onEdit={onEdit}
        onDelete={onDelete}
        newswireServices={newswireServices}
        newswireFormats={formatList}
      />
      {editNewswireService && (
        <Dialog
          onClose={onEditCancel}
          aria-labelledby="edit-newswire-dialog"
          open
        >
          <Form
            newswireService={editNewswireService}
            newswireFormats={formatList}
            onSave={onSave}
            onCancel={onEditCancel}
          />
        </Dialog>
      )}
      {showNewswireService && (
        <ItemDetail
          newswireServiceItem={showNewswireService}
          formatName={findFormatName(showNewswireService.format)}
          onClose={onClose}
        />
      )}
    </ContentBox>
  );
};

export default Newswires;
